// src/components/page-content-v4/hooks/usePageForm.ts
import { useState, useEffect, useCallback } from 'react';
import { getTemplateById } from '@/services/templateService';
import { getAccountInfo } from '@/services/accountService';
import { useNavigate } from 'react-router-dom'; 
import { 
  getPageContentByTemplateId, 
  getPageContentById, 
  createPageContent, 
  updatePageContent, 
  publishDraft,
  discardDraft as discardDraftService
} from '@/services/pageService';
import { Template, PageData, AccountInfo } from '../types';

export const usePageForm = (templateId: string, pageId?: string, selectedLanguage: string = 'en') => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [page, setPage] = useState<PageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSeoEnabled, setIsSeoEnabled] = useState(false);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [isDraftEditing, setIsDraftEditing] = useState(false);
  const [fields, setFields] = useState({})
  const initializeNewPage = (contentGroupId = '', isSinglePage = false) => ({
    contentGroupId,
    templateId,
    title: '',
    content: {},
    seo: { metaTitle: '', metaDescription: '', canonicalUrl: '' },
    language: selectedLanguage,
    isSinglePage
  });

  const fetchAccountInfo = useCallback(async () => {
    try {
      const result = await getAccountInfo();
      if (result.success) setAccountInfo(result.data);
    } catch (err) {
      console.error('Failed to fetch account info:', err);
    }
  }, []);

  const fetchTemplateAndPage = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsDraftEditing(false);
      await fetchAccountInfo();

      const templateRes = await getTemplateById(templateId);
      const foundTemplate = templateRes.data;
      setTemplate(foundTemplate);

      setIsSeoEnabled(foundTemplate.needsSeo);
      setFields(foundTemplate.fields)
      let pageData;
      if (foundTemplate.isSinglePage) {
        const pageRes = await getPageContentByTemplateId(templateId, selectedLanguage);
        pageData = pageRes.data || initializeNewPage(pageRes.data?.contentGroupId, true);
      } else if (pageId && pageId !== 'new') {
        const pageRes = await getPageContentById(pageId, selectedLanguage);
        if (pageRes.success) {
          pageData = pageRes.data?.isNew ? initializeNewPage(pageRes.data.contentGroupId) : pageRes.data;
        } else {
          setError(new Error(pageRes.data));
          navigate(`/page-content/${templateId}`);
        }
      } else {
        pageData = initializeNewPage();
      }
      // if the pageData contains draft then set datft as setPage
      if(pageData.draft){
        setPage(pageData.draft);
        setIsDraftEditing(true);
      } else {
        setPage(pageData);
        setIsDraftEditing(false);
      }
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, pageId, selectedLanguage, fetchAccountInfo]);

  useEffect(() => {
    fetchTemplateAndPage();
  }, [fetchTemplateAndPage]);

  const reloadPageData = useCallback(async () => {
    try {
      await fetchTemplateAndPage(); // Re-fetch template and page data
    } catch (err) {
      console.error("Error reloading page data:", err);
    }
  }, [fetchTemplateAndPage]);

  const savePage = async (isDraft: boolean) => {
    if (!page) return;

    const saveObject: any = {
      title: page.title,
      content: page.content,
      seo: isSeoEnabled ? page.seo : { metaTitle: '', metaDescription: '', canonicalUrl: '', keywords: [] },
      templateId: page.templateId,
      pageId: page.pageId,
      pageKey: page.pageKey,
      language: selectedLanguage,
      contentGroupId: page.contentGroupId,
      isPrimary: page.isPrimary,
      isDraft,
    };

    if (page.pageId) {
      saveObject.contentFields = fields
      await updatePageContent(saveObject);
    } else {
      saveObject.pageKey = template?.pageKey || '';
      await createPageContent(saveObject);
    }
  };

  const handlePublishDraft = async () => {
    if (!page) return;
    await publishDraft(page.pageId!, selectedLanguage);
  };

  const discardDraft = async () => {
    if (!page?.pageId) return;
    try {
      await discardDraftService(page.pageId);
      navigate(`/page-content/${templateId}`);
    } catch (error) {
      console.error('Error discarding draft:', error);
      throw error;
    }
  };

  return { template, page, setPage, isLoading, error, savePage, isSeoEnabled, accountInfo, isDraftEditing, handlePublishDraft, reloadPageData, discardDraft };
};
